import { useEffect, useState } from 'react'

import { Button } from 'components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog'

import { MAP_VIEWS, cameraFrom, type MapCamera } from '../blocks/map/MapBlocks'

/**
 * "Frame this view" for a map block (MYD-500): pan, zoom, rotate and tilt a
 * live copy of the block's map, then keep that camera in the block.
 *
 * ## Why the zoom is converted
 *
 * The block is drawn at its region's size on the 1280x720 stage, then scaled
 * to fit the screen. So the slide's camera is a camera for THAT size, and
 * reproduces exactly at any stage scale. The dialog's map has the region's
 * shape but not its size: shown at a scale `s`, the same view needs zoom
 * + log2(s) here, and a camera captured here needs - log2(s) to be stored.
 * Centre, bearing and pitch do not depend on size.
 */

export interface RegionSize {
	width: number
	height: number
}

/** The largest box of the region's shape that fits the dialog. */
const MAX_BOX = { width: 880, height: 520 }

export const dialogScale = (region: RegionSize) => Math.min(MAX_BOX.width / region.width, MAX_BOX.height / region.height, 1.5)

/** A stored (region-size) camera as the dialog's map must show it. */
export const toDialogCamera = (camera: MapCamera, scale: number): MapCamera => ({ ...camera, zoom: camera.zoom + Math.log2(scale) })

/** A camera captured in the dialog as the region must store it. */
export const toRegionCamera = (camera: MapCamera, scale: number): MapCamera => ({
	...camera,
	zoom: Math.round((camera.zoom - Math.log2(scale)) * 100) / 100,
})

interface FrameMapDialogProps {
	open: boolean
	onClose: () => void
	element: string
	/** The block's resolved payload: what the slide shows, to frame against. */
	payload: any
	region: RegionSize
	onSave: (camera: MapCamera) => void
}

export function FrameMapDialog({ open, onClose, element, payload, region, onSave }: FrameMapDialogProps) {
	const View = MAP_VIEWS[element]
	const scale = dialogScale(region)
	const stored = cameraFrom(payload?.camera)
	const [captured, setCaptured] = useState<MapCamera | null>(null)

	useEffect(() => {
		if (open) setCaptured(null)
	}, [open])

	if (!View) return null

	// The dialog opens on the block's own framing, converted to this size, or
	// fits the content as the slide does when the block has not been framed.
	const initial = stored ? toDialogCamera(stored, scale) : null
	const shown = { ...payload, camera: initial }

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent className="sm:max-w-[960px]">
				<DialogHeader>
					<DialogTitle>Frame this view</DialogTitle>
					<DialogDescription>
						Drag to move, scroll to zoom, right-drag to rotate and tilt. The slide shows exactly this view.
					</DialogDescription>
				</DialogHeader>
				<div className="flex justify-center">
					<div style={{ width: region.width * scale, height: region.height * scale }} className="overflow-hidden rounded-lg border border-gray-200">
						{open && <View payload={shown} interactive onCameraChange={setCaptured} />}
					</div>
				</div>
				<DialogFooter>
					<Button variant="ghost" onClick={onClose}>
						Cancel
					</Button>
					<Button
						variant="primary"
						disabled={!captured}
						onClick={() => {
							if (!captured) return
							onSave(toRegionCamera(captured, scale))
							onClose()
						}}
					>
						Use this view
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default FrameMapDialog
